<template>
  <q-list>
    <sticky-header static color="black">
      Sort
    </sticky-header>

    <q-item-wrapper>
      <q-select
        v-model="filterVals.sortBy"
        :options="sortByOptions"
        outlined
        stack-label
        label="Sort By"
        emit-value
        map-options
        behavior="menu"
      />
    </q-item-wrapper>

    <q-item-wrapper v-if="!['relevancy', 'closest'].includes(filterVals.sortBy)">
      <q-select
        v-model="filterVals.sortDirection"
        :options="orderByOptions"
        outlined
        stack-label
        label="Order"
        emit-value
        map-options
        behavior="menu"
      />
    </q-item-wrapper>

    <sticky-header static color="black">
      Filters
    </sticky-header>

    <q-item-wrapper>
      <q-select
        v-model="filterVals.status"
        map-options
        emit-value
        outlined
        stack-label
        label="Appointment Status"
        class="full-width"
        behavior="menu"
        :options="filterStatusOptions"
        :clearable="!!filterVals.status"
      >
        <template #option="scope">
          <q-select-color-item
            v-bind="scope.itemProps"
            :label="scope.opt.label"
            :color="scope.opt.hex_color"
            type="radio"
            hex
          />
        </template>
      </q-select>
    </q-item-wrapper>

    <q-item-wrapper>
      <client-input-popover
        v-model="filterVals.clients"
        outlined
        multiple
        clearable
        label="Filter by clients"
        placeholder="Start typing..."
        input-class="full-width"
        :disable="restrictedToClient"
      />
    </q-item-wrapper>

    <q-item-wrapper v-if="can(permissions.canEditAppointments)">
      <user-input-popover
        v-model="filterVals.attendees"
        outlined
        multiple
        clearable
        label="Filter by attendees"
        placeholder="Start typing..."
        input-class="full-width"
        :disable="restrictedToAttendees"
      />
    </q-item-wrapper>

    <q-item-wrapper>
      <TypesPopover
        v-model="filterVals.appointmentTypes"
        :dense="false"
        :stack-label="true"
        :disable="restrictedToTypes"
        label="Appointment Types"
        placeholder="Start typing..."
        clearable
        multiple
        outlined
        input-class="full-width"
      />
    </q-item-wrapper>

    <q-item-wrapper>
      <q-item-label
        caption
        class="q-mb-sm"
      >
        Appointment Date Range
      </q-item-label>

      <date-picker
        v-model="filterVals.startDate"
        :max="filterVals.endDate"
        :debounce="1000"
        label="Start Date"
        clearable
        outlined
        class="q-mb-sm"
      />

      <date-picker
        v-model="filterVals.endDate"
        :min="filterVals.startDate"
        :debounce="1000"
        label="End Date"
        clearable
        outlined
        class="q-mb-sm"
      />
    </q-item-wrapper>

    <q-separator spaced inset />

    <q-expansion-item
      v-model="showAdvancedFilters"
      label="Show advanced options"
      header-class="text-bold initial-line-height"
      expand-icon="mdi-chevron-right"
      expanded-icon="mdi-chevron-down"
      :dense="!$q.platform.has.touch"
      @update:model-value="$emit('scrollToBottom', $event)"
    >
      <q-list>
        <q-item-wrapper tag="label">
          <q-checkbox
            v-model="filterVals.allDay"
            label="All Day Appointments"
          />
        </q-item-wrapper>

        <q-item-wrapper>
          <q-item-label
            caption
            class="q-mb-sm"
          >
            Appointment Colour
          </q-item-label>

          <color-field
            v-model="filterVals.color"
            clearable
          />
        </q-item-wrapper>

        <q-item-wrapper>
          <timezones
            v-model="filterVals.timezone"
            label="Appointment Timezone"
            outlined
            stack-label
            clearable
          />
        </q-item-wrapper>

        <q-item-wrapper v-if="can(permissions.canEditAppointments)">
          <user-input-popover
            v-model="filterVals.creator"
            outlined
            label="Filter by creator"
            placeholder="Start typing..."
            class="full-width"
          />
        </q-item-wrapper>
      </q-list>
    </q-expansion-item>

    <q-separator spaced inset />

    <q-item-wrapper class="sticky-position blurred-container bg-drawer" style="bottom: 0;">
      <q-btn
        label="Apply Filters"
        color="teal"
        :loading="loading"
        size="12px"
        class="full-width"
        @click="applyFilters"
      />
    </q-item-wrapper>
  </q-list>
</template>

<script>
import statusMixin from 'components/app/calendar/partials/mixins/statusMixin'
import TypesPopover from 'components/app/calendar/types/TypesPopover.vue'

export default {
  components: { TypesPopover },
  mixins: [statusMixin],
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    filterVals: {
      type: Object,
      required: true
    },
    isScheduled: {
      type: Boolean,
      default: false
    },
    restrictedToClient: {
      type: Boolean,
      default: false
    },
    restrictedToAttendees: {
      type: Boolean,
      default: false
    },
    restrictedToTypes: {
      type: Boolean,
      default: false
    }
  },
  emits: ['applyFilters', 'scrollToBottom'],
  data () {
    return {
      showAdvancedFilters: false,
      sortByOptions: [
        // Relevancy disabled. Results are not returned in an appropriate date order
        // { label: 'Relevancy', value: 'relevancy' },
        { label: 'Closest to current date', value: 'closest' },
        { label: 'Start date', value: 'start_date' },
        { label: 'Creation date', value: 'created_at' }
      ],
      orderByOptions: [
        { label: 'Ascending', value: 'asc' },
        { label: 'Descending', value: 'desc' }
      ]
    }
  },
  computed: {
    filterStatusOptions () {
      return this.baseStatusOptions(true, !this.isScheduled)
    }
  },
  methods: {
    applyFilters () {
      this.$emit('applyFilters')
    }
  }
}
</script>
